import { useQuery, useQueryClient } from '@tanstack/react-query'
import { logOffSession, sessionsPageQuery, type DeviceLogin, type DeviceSession } from '../api/devices'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Active Sessions - opteemum/devices/details/currentSessions.php plus
 * _partials/device_logins.php. SID pe Windows, UID pe Linux; roaming numai
 * pe Windows. Log Off posteaza aceleasi campuri ca Opteemum::log_off_user.
 */

export function DeviceSessions({ id }: ScreenProps) {
  const page = useQuery(sessionsPageQuery(id))
  const query = useQueryClient()

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <div className="device-sessions">
      {page.data.sessions.length === 0 ? (
        <div className="alert alert-info">
          <strong>
            <i className="fas fa-info-circle"></i>
          </strong>{' '}
          No active sessions.
        </div>
      ) : (
        page.data.sessions.map((session) => (
          <SessionCard
            key={session.id}
            session={session}
            deviceId={id}
            onLogOff={async () => {
              await logOffSession(id, session.user_name, session.id)
              toast('Log off instruction sent.', 'Success')
              await query.invalidateQueries({ queryKey: ['device', id, 'sessions'] })
            }}
          />
        ))
      )}
      <RecentLogins logins={page.data.logins} />
    </div>
  )
}

function SessionCard({
  session,
  deviceId,
  onLogOff,
}: {
  session: DeviceSession
  deviceId: number
  onLogOff(): Promise<void>
}) {
  const title = (session.domain ? `${session.domain}\\` : '') + session.user_name
  const off = session.logged_off

  return (
    <div className="row">
      <div className=" col-md-3">
        <div className="bg-metal color-white current_sessions">
          <div className={`user-icon ${off ? 'color-red' : 'color-green2'} color-white round`}>
            <i className="icon-user"></i>
          </div>
          <h4 style={{ fontWeight: 'normal', marginBottom: 10, color: '#ddd' }}>{title}</h4>
          <ul className="color-white" style={{ fontSize: 10 }}>
            <li>
              <label className="color-quartz">{session.sid_label}:</label> {session.sid}
            </li>
            <li>
              <label className="color-quartz">LOGIN TYPE:</label> {session.login_type}
            </li>
            <li>
              <label className="color-quartz">LOGON ID:</label> {session.logon_id}
            </li>
            <li>
              <label className="color-quartz">LOCAL PATH:</label> {session.local_path}
            </li>
            {session.windows_profile && (
              <>
                <li>
                  <label className="color-quartz">LOCAL PATH SIZE:</label> {session.local_path_size}
                </li>
                <li>
                  <label className="color-quartz">ROAMING PROFILE:</label> {session.roaming_profile}
                </li>
                <li>
                  <label className="color-quartz">ROAMING PROFILE PATH:</label> {session.roaming_profile_path}
                </li>
                <li>
                  <label className="color-quartz">ROAMING PROFILE PATH SIZE:</label> {session.roaming_profile_path_size}
                </li>
              </>
            )}
          </ul>
          <form
            action="/opteemum/log_off_user"
            method="post"
            className="log_off_user"
            onSubmit={(e) => {
              e.preventDefault()
              if (!off) void onLogOff()
            }}
          >
            <input type="hidden" name="username" value={session.user_name} />
            <input type="hidden" name="device_id" value={deviceId} />
            <input type="hidden" name="session_id" value={session.id} />
            <button type="submit" className="btn-outline btn white" disabled={off} style={off ? { backgroundColor: '#ccc' } : undefined}>
              <i className="fas fa-power-off"></i> Log Off
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

function RecentLogins({ logins }: { logins: DeviceLogin[] }) {
  return (
    <div className="row" style={{ marginTop: 18 }}>
      <div className="col-md-12">
        <h4 className="bordered">Recent logins</h4>
        {logins.length === 0 ? (
          <p className="color-light-grey" style={{ fontSize: 12 }}>
            <i className="fas fa-info-circle"></i> Nothing recorded yet. The log is built from what changes between two
            reports, so it starts filling in from the machine&apos;s next one.
          </p>
        ) : (
          <table className="table table-hover" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>User</th>
                <th style={{ width: 170 }}>Logged in</th>
                <th style={{ width: 170 }}>Logged out</th>
                <th style={{ width: 120 }}>For</th>
              </tr>
            </thead>
            <tbody>
              {logins.map((login, i) => (
                <tr key={`${login.user}-${login.at}-${i}`}>
                  <td>
                    {login.user}
                    {login.open && (
                      <>
                        {' '}
                        <i
                          className="fas fa-circle color-green2 font-10"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Still logged in at the last report"
                        ></i>
                      </>
                    )}
                    {login.type !== '' && (
                      <div className="color-light-grey" style={{ fontSize: 11 }}>
                        {login.type}
                      </div>
                    )}
                  </td>
                  <td className="color-light-grey">{login.at}</td>
                  <td className="color-light-grey">
                    {login.open ? <span className="color-green2">still on</span> : login.left_at}
                  </td>
                  <td className="color-light-grey">{login.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
